import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { DeviceModel, DeviceResponse } from '@gtpl/shared-models/masters';
import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository } from 'typeorm';
import { PlantIdRequest } from '../unit-codes/dto/plant-id.request';
import { Device } from './device.entity';
import { DeviceCategoryAndDeptNameRequest } from './dto/device-category-and-dept.request';
import { DeviceNameDto } from './dto/device-name.dto';
import { DeviceUpdateDto } from './dto/device-update.dto';
import { DeviceDto } from './dto/device.dto';
import { MacIdDto } from './dto/mac-id.dto';

@Injectable()
export class DeviceService {
    constructor(@InjectRepository(Device) private repository: Repository<Device>) {
    }

    /**
     * Service to create new device
     * @param deviceDto 
     * @returns 
     */
    async createDevice(deviceDto: DeviceDto): Promise<DeviceResponse> {
        const deviceResult = await this.validateDeviceExistsOrNot(deviceDto.deviceName);
        if (deviceResult) {
            throw new ErrorResponse(10000, 'Device  already exists');
        } else {
            const deviceEntity = new Device();
            deviceEntity.deviceName = deviceDto.deviceName;
            deviceEntity.deviceType = deviceDto.deviceType;
            deviceEntity.deviceMacId = deviceDto.deviceMacId;
            deviceEntity.deviceStatus = deviceDto.deviceStatus;
            deviceEntity.plantId = deviceDto.plantId;
            deviceEntity.createdUser = deviceDto.createdUser;
            const deviceResponse = await this.repository.save(deviceEntity);
            if (deviceResponse) {
                return new DeviceResponse(true, 10000, 'Device Details Created Successfully');
            }
        }
    }

    /**
     * validate if a device is existed or not
     * @param deviceName 
     * @returns boolean true/false
     */
    async validateDeviceExistsOrNot(deviceName: string): Promise<boolean> {
        const deviceResponse = await this.repository.findOne({
            where: { deviceName: deviceName },
        });
        if (deviceResponse) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Service to update a device details
     * @param deviceDto 
     * @returns 
     */
    async updateDeviceStatus(deviceDto: DeviceUpdateDto): Promise<DeviceResponse> {
        const deviceEntity = await this.repository.findOne({
            where: { deviceMacId: deviceDto.deviceMacId },
        });
        if (!deviceEntity) {
            throw new ErrorResponse(10000, 'Device details not found');
        } else {
            deviceEntity.deviceMacId = deviceDto.deviceMacId;
            deviceEntity.deviceStatus = deviceDto.deviceStatus;
            deviceEntity.plantId = deviceDto.plantId;
            deviceEntity.updatedUser = deviceDto.updatedUser;
            deviceEntity.lastReportedTime = deviceDto.lastReportedTime;
            deviceEntity.deviceErrorMsg = deviceDto.deviceErrorMsg;
            const deviceResponse = await this.repository.save(deviceEntity);
            if (deviceResponse) {
                return new DeviceResponse(true, 10000, 'Device Details Updated Successfully');
            }
        }
    }

    /**
     * Service to get all the devices
     * @returns 
     */
    async getAllDevices(): Promise<DeviceResponse> {
        const deviceResponse = await this.repository.find({
            order: { deviceId: "ASC" },
        });

        const deviceDetails: DeviceModel[] = [];
        if (deviceResponse.length === 0) {
            throw new ErrorResponse(9999, 'Device details not found');
        }
        for (const device of deviceResponse) {
            const deviceModel = new DeviceModel(device.plantId, device.deviceName, device.deviceType, device.deviceMacId, device.deviceStatus, device.lastReportedTime, device.deviceErrorMsg, device.deptName, device.deptCode, device.module, device.secretKey);
            deviceDetails.push(deviceModel);
        }
        return new DeviceResponse(true, 10000, 'Device Details Retrived Successfully', deviceDetails);

    }

    /**
     * Service to get all the devices by plant id
     * @param plantIdReq
     * @returns 
     */
    async getDeviceStatusByPlantId(plantIdReq: PlantIdRequest): Promise<DeviceResponse> {
        const deviceResponse = await this.repository.find({
            where: { plantId: plantIdReq.plantId },
            order: { deviceId: "ASC" },
        });

        const deviceDetails: DeviceModel[] = [];
        if (deviceResponse.length === 0) {
            throw new ErrorResponse(9999, 'Device details not found');
        }
        for (const device of deviceResponse) {
            const deviceModel = new DeviceModel(device.plantId, device.deviceName, device.deviceType, device.deviceMacId, device.deviceStatus, device.lastReportedTime, device.deviceErrorMsg, device.deptName, device.deptCode, device.module, device.secretKey);
            deviceDetails.push(deviceModel);
        }
        return new DeviceResponse(true, 10000, 'Device Details Retrived Successfully', deviceDetails);
    }

    /**
     * 
     * @param macIdDto 
     * @returns 
     */
    async getDeviceStatusByPlantIdAndMacId(macIdDto: MacIdDto): Promise<DeviceResponse> {
        const deviceResponse = await this.repository.find({
            where: { plantId: macIdDto.plantId, deviceMacId: macIdDto.deviceMacId },
            order: { deviceId: "ASC" },
        });

        const deviceDetails: DeviceModel[] = [];
        if (deviceResponse.length === 0) {
            throw new ErrorResponse(9999, 'Device details not found');
        }
        for (const device of deviceResponse) {
            const deviceModel = new DeviceModel(device.plantId, device.deviceName, device.deviceType, device.deviceMacId, device.deviceStatus, device.lastReportedTime, device.deviceErrorMsg, device.deptName, device.deptCode, device.module, device.secretKey);
            deviceDetails.push(deviceModel);
        }
        return new DeviceResponse(true, 10000, 'Device Details Retrived Successfully', deviceDetails);
    }


    /**
     * gets all the devices for the given device category and dept name and plant
     * @param deviceCatAndDeptNameReq 
     * @returns 
     */
    async getAllDevicesByDeviceCategoryAndDepartment(deviceCatAndDeptNameReq: DeviceCategoryAndDeptNameRequest): Promise<DeviceResponse> {
        const deviceResponse = await this.repository.find({
            where: { plantId: deviceCatAndDeptNameReq.plantId, deviceCategory: deviceCatAndDeptNameReq.deviceCategory, deptName: deviceCatAndDeptNameReq.deptName, isActive: true },
            order: { deviceId: "ASC" },
        });
        const deviceDetails: DeviceModel[] = [];
        if (deviceResponse.length === 0) {
            throw new ErrorResponse(9999, 'Device details not found');
        }
        for (const device of deviceResponse) {
            const deviceModel = new DeviceModel(device.plantId, device.deviceName, device.deviceType, device.deviceMacId, device.deviceStatus, device.lastReportedTime, device.deviceErrorMsg, device.deptName, device.deptCode, device.module, device.secretKey);
            deviceDetails.push(deviceModel);
        }
        return new DeviceResponse(true, 10000, 'Device Details Retrived Successfully', deviceDetails);
    }

    async getDeviceDataByMacId(macIdDto: DeviceNameDto): Promise<DeviceResponse> {
        let deviceResponse = await this.repository.find({
                where: { deviceMacId: macIdDto.deviceMacId },
                order: { deviceId: "ASC" },
            });
        console.log(deviceResponse)

        const deviceDetails: DeviceModel[] = [];
        if (deviceResponse.length === 0) {
            throw new ErrorResponse(9999, 'Device details not found');
        }
        for (const device of deviceResponse) {
            const deviceModel = new DeviceModel(device.plantId, device.deviceName, device.deviceType, device.deviceMacId, device.deviceStatus, device.lastReportedTime, device.deviceErrorMsg, device.deptName, device.deptCode, device.module, device.secretKey);
            deviceDetails.push(deviceModel);
        }
        return new DeviceResponse(true, 10000, 'Device Details Retrived Successfully', deviceDetails);
    }

}
